package dev.wyns.ventrova.ui.web.cupertino

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectVerticalDragGestures
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.kyant.backdrop.Backdrop
import com.kyant.backdrop.backdrops.layerBackdrop
import com.kyant.backdrop.backdrops.rememberLayerBackdrop
import dev.wyns.ventrova.AppDependencies
import dev.wyns.ventrova.data.model.VeganLevel
import dev.wyns.ventrova.platform.BackNavigation
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.filter_clear
import dev.wyns.ventrova.resources.filter_open_now
import dev.wyns.ventrova.resources.filters
import dev.wyns.ventrova.resources.ic_add_location_alt
import dev.wyns.ventrova.resources.ic_cancel_filled
import dev.wyns.ventrova.resources.ic_close
import dev.wyns.ventrova.resources.ic_favorite
import dev.wyns.ventrova.resources.ic_favorite_filled
import dev.wyns.ventrova.resources.ic_ios_share
import dev.wyns.ventrova.resources.ic_map
import dev.wyns.ventrova.resources.ic_map_filled
import dev.wyns.ventrova.resources.ic_near_me_filled
import dev.wyns.ventrova.resources.ic_person
import dev.wyns.ventrova.resources.ic_person_filled
import dev.wyns.ventrova.resources.ic_schedule
import dev.wyns.ventrova.resources.ic_search
import dev.wyns.ventrova.resources.ic_tune
import dev.wyns.ventrova.resources.search
import dev.wyns.ventrova.resources.search_placeholder
import dev.wyns.ventrova.resources.suggest_cancel
import dev.wyns.ventrova.resources.suggest_place
import dev.wyns.ventrova.resources.suggest_send
import dev.wyns.ventrova.resources.tab_favorites
import dev.wyns.ventrova.resources.tab_map
import dev.wyns.ventrova.resources.tab_profile
import dev.wyns.ventrova.ui.components.DrawnPlatformControls
import dev.wyns.ventrova.ui.components.LevelGlyph
import dev.wyns.ventrova.ui.components.VIcon
import dev.wyns.ventrova.ui.components.bodyStyle
import dev.wyns.ventrova.ui.cupertino.CupertinoDetailContent
import dev.wyns.ventrova.ui.cupertino.CupertinoFavoritesContent
import dev.wyns.ventrova.ui.cupertino.CupertinoFilterContent
import dev.wyns.ventrova.ui.cupertino.CupertinoMapContent
import dev.wyns.ventrova.ui.cupertino.CupertinoModel
import dev.wyns.ventrova.ui.cupertino.CupertinoOnboardingContent
import dev.wyns.ventrova.ui.cupertino.CupertinoPanelContent
import dev.wyns.ventrova.ui.cupertino.CupertinoProfileContent
import dev.wyns.ventrova.ui.cupertino.CupertinoSearchContent
import dev.wyns.ventrova.ui.cupertino.CupertinoStyle
import dev.wyns.ventrova.ui.cupertino.CupertinoSuggestContent
import dev.wyns.ventrova.ui.cupertino.CupertinoTheme
import dev.wyns.ventrova.ui.cupertino.PanelDetent
import dev.wyns.ventrova.ui.theme.VentrovaColors
import dev.wyns.ventrova.ui.theme.messageText
import dev.wyns.ventrova.ui.theme.palette
import dev.wyns.ventrova.ui.theme.shortLabel
import dev.wyns.ventrova.ui.web.interFontFamily
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

private enum class CupertinoTab(val label: StringResource, val icon: DrawableResource, val selectedIcon: DrawableResource) {
    Map(Res.string.tab_map, Res.drawable.ic_map, Res.drawable.ic_map_filled),
    Favorites(Res.string.tab_favorites, Res.drawable.ic_favorite, Res.drawable.ic_favorite_filled),
    Profile(Res.string.tab_profile, Res.drawable.ic_person, Res.drawable.ic_person_filled),
    Search(Res.string.search, Res.drawable.ic_search, Res.drawable.ic_search),
}

/** Drawn iOS-style controls and Inter, the closest open alternative to SF Pro. */
private val WebCupertinoStyle = CupertinoStyle(controls = DrawnPlatformControls(), bodyFont = { interFontFamily() })

/** The iOS design for iOS browsers: shared Cupertino content with an imitated Liquid Glass chrome. */
@Composable
fun WebCupertinoApp(dependencies: AppDependencies, backNavigation: BackNavigation) {
    val model = remember { CupertinoModel(dependencies, WebCupertinoStyle) }
    CupertinoTheme(model) {
        WebCupertinoScreen(model, backNavigation)
    }
}

@Composable
private fun WebCupertinoScreen(model: CupertinoModel, back: BackNavigation) {
    val state by model.viewModel.state.collectAsState()
    val viewModel = model.viewModel
    var tab by remember { mutableStateOf(CupertinoTab.Map) }
    var filtersOpen by remember { mutableStateOf(false) }
    var suggestOpen by remember { mutableStateOf(false) }
    model.onOpenFilters = { filtersOpen = true }
    model.onCloseFilters = { filtersOpen = false }
    model.onOpenSuggest = { suggestOpen = true }
    model.onCloseSuggest = {
        suggestOpen = false
        viewModel.resetSuggestion()
    }

    back.BackHandler(enabled = tab != CupertinoTab.Map) { tab = CupertinoTab.Map }
    back.BackHandler(enabled = state.detail != null) { viewModel.closeDetail() }
    back.BackHandler(enabled = filtersOpen) { filtersOpen = false }
    back.BackHandler(enabled = suggestOpen) { model.onCloseSuggest() }

    BoxWithConstraints(Modifier.fillMaxSize().background(VentrovaColors.MapBackground)) {
        val height = maxHeight
        val backdrop = rememberLayerBackdrop()

        // Everything the glass refracts: the map and the tab pages.
        Box(Modifier.fillMaxSize().layerBackdrop(backdrop)) {
            CupertinoMapContent(model)
            when (tab) {
                CupertinoTab.Map -> Unit
                CupertinoTab.Favorites -> TabPage(stringResource(Res.string.tab_favorites), VentrovaColors.Page) { CupertinoFavoritesContent(model) }
                CupertinoTab.Profile -> TabPage(stringResource(Res.string.tab_profile), VentrovaColors.Grouped) { CupertinoProfileContent(model) }
                CupertinoTab.Search -> TabPage(stringResource(Res.string.search), VentrovaColors.Page, header = { SearchField(state.query, viewModel::setQuery) }) {
                    CupertinoSearchContent(model)
                }
            }
        }

        if (tab == CupertinoTab.Map) {
            FilterChips(model, backdrop, Modifier.padding(top = 12.dp))
            MapButtons(model, backdrop, Modifier.align(Alignment.TopEnd).padding(top = 64.dp, end = 12.dp))
            PlacesPanel(model, backdrop, height, Modifier.align(Alignment.BottomCenter).padding(start = 8.dp, end = 8.dp, bottom = 96.dp))
        }

        TabBar(tab, onSelect = { tab = it; viewModel.selectPlace(null) }, backdrop = backdrop, modifier = Modifier.align(Alignment.BottomCenter))

        Sheet(
            visible = state.detail != null,
            onDismiss = viewModel::closeDetail,
            background = VentrovaColors.Paper,
            overlay = { sheetBackdrop ->
                Row(Modifier.align(Alignment.TopEnd).padding(top = 16.dp, end = 14.dp), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    val detail = state.detail
                    GlassIconButton(Res.drawable.ic_ios_share, sheetBackdrop) { detail?.let { model.dependencies.externalActions.share(it.place) } }
                    GlassIconButton(
                        if (detail?.isFavorite == true) Res.drawable.ic_favorite_filled else Res.drawable.ic_favorite,
                        sheetBackdrop,
                        tint = if (detail?.isFavorite == true) VentrovaColors.Favorite else VentrovaColors.Ink,
                    ) { detail?.let { viewModel.toggleFavorite(it.id) } }
                    GlassIconButton(Res.drawable.ic_close, sheetBackdrop) { viewModel.closeDetail() }
                }
            },
        ) {
            CupertinoDetailContent(model)
        }

        Sheet(visible = filtersOpen, onDismiss = { filtersOpen = false }, background = VentrovaColors.Paper, floating = true) {
            Column {
                SheetHeader(
                    title = stringResource(Res.string.filters),
                    leading = { TextAction(stringResource(Res.string.filter_clear), VentrovaColors.Accent, viewModel::clearFilters) },
                    trailing = { CloseCircle { filtersOpen = false } },
                )
                CupertinoFilterContent(model)
            }
        }

        Sheet(visible = suggestOpen, onDismiss = model.onCloseSuggest, background = VentrovaColors.Grouped) {
            Column {
                SheetHeader(
                    title = stringResource(Res.string.suggest_place),
                    leading = { PillAction(stringResource(Res.string.suggest_cancel), Color.White.copy(alpha = 0.8f), VentrovaColors.Ink, onClick = model.onCloseSuggest) },
                    trailing = {
                        if (!state.suggestion.sent) {
                            PillAction(
                                stringResource(Res.string.suggest_send),
                                if (state.suggestion.canSend) VentrovaColors.Ink else Color(0xFFB5B6B0),
                                Color.White,
                                bold = true,
                                onClick = viewModel::sendSuggestion,
                            )
                        }
                    },
                )
                CupertinoSuggestContent(model)
            }
        }

        if (state.showOnboarding) CupertinoOnboardingContent(model)

        state.message?.let { message ->
            Toast(messageText(message.kind), onShown = { viewModel.consumeMessage(message) }, modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = 104.dp))
        }
    }
}

@Composable
private fun TabPage(title: String, background: Color, header: (@Composable () -> Unit)? = null, content: @Composable () -> Unit) {
    Column(Modifier.fillMaxSize().background(background)) {
        Text(title, style = bodyStyle(34.sp, FontWeight.Bold), modifier = Modifier.padding(start = 20.dp, end = 20.dp, top = 24.dp, bottom = 10.dp))
        header?.let { Box(Modifier.padding(horizontal = 16.dp)) { it() } }
        Box(Modifier.weight(1f)) { content() }
    }
}

@Composable
private fun SearchField(query: String, onQuery: (String) -> Unit) {
    Row(
        Modifier.fillMaxWidth().height(44.dp).background(VentrovaColors.Ink.copy(alpha = 0.07f), RoundedCornerShape(50)).padding(horizontal = 14.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        VIcon(Res.drawable.ic_search, size = 20.dp, tint = VentrovaColors.Muted)
        Box(Modifier.weight(1f)) {
            if (query.isEmpty()) Text(stringResource(Res.string.search_placeholder), style = bodyStyle(17.sp, color = VentrovaColors.Faint))
            BasicTextField(query, onQuery, singleLine = true, textStyle = bodyStyle(17.sp), cursorBrush = SolidColor(VentrovaColors.Accent), modifier = Modifier.fillMaxWidth())
        }
        if (query.isNotEmpty()) VIcon(Res.drawable.ic_cancel_filled, size = 18.dp, tint = VentrovaColors.Faint, modifier = Modifier.clickable { onQuery("") })
    }
}

@Composable
private fun FilterChips(model: CupertinoModel, backdrop: Backdrop, modifier: Modifier) {
    val state by model.viewModel.state.collectAsState()
    val filters = state.filters
    Row(
        modifier.fillMaxWidth().horizontalScroll(rememberScrollState()).padding(horizontal = 12.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        val sheetCount = filters.sheetOnlyCount
        GlassChip(backdrop, active = sheetCount > 0, activeSurface = VentrovaColors.Ink, activeContent = Color.White, onClick = model.onOpenFilters) {
            VIcon(Res.drawable.ic_tune, size = 19.dp)
            Text(stringResource(Res.string.filters), style = chipStyle())
            if (sheetCount > 0) {
                Box(Modifier.size(19.dp).background(Color.White, CircleShape), contentAlignment = Alignment.Center) {
                    Text("$sheetCount", style = bodyStyle(11.5.sp, FontWeight.Bold))
                }
            }
        }
        VeganLevel.entries.forEach { level ->
            val active = level in filters.levels
            GlassChip(backdrop, active = active, activeSurface = level.palette.soft, activeContent = level.palette.ink, onClick = { model.viewModel.toggleLevel(level) }) {
                LevelGlyph(level, size = 12.dp)
                Text(stringResource(level.shortLabel), style = chipStyle())
                if (!state.isLoading) {
                    Text("${state.levelCounts[level] ?: 0}", style = chipStyle().copy(color = LocalContentColor.current.copy(alpha = 0.5f), fontWeight = FontWeight.Medium))
                }
            }
        }
        GlassChip(backdrop, active = filters.openNow, activeSurface = VentrovaColors.Ink, activeContent = Color.White, onClick = model.viewModel::toggleOpenNow) {
            VIcon(Res.drawable.ic_schedule, size = 18.dp)
            Text(stringResource(Res.string.filter_open_now), style = chipStyle())
        }
    }
}

@Composable
private fun chipStyle() = bodyStyle(14.5.sp, FontWeight.SemiBold, LocalContentColor.current)

@Composable
private fun GlassChip(
    backdrop: Backdrop,
    active: Boolean,
    activeSurface: Color,
    activeContent: Color,
    onClick: () -> Unit,
    content: @Composable () -> Unit,
) {
    CompositionLocalProvider(LocalContentColor provides if (active) activeContent else VentrovaColors.Ink) {
        Row(
            Modifier
                .height(38.dp)
                .liquidGlass(backdrop, surface = if (active) activeSurface.copy(alpha = 0.92f) else Color.White.copy(alpha = 0.4f))
                .glassClickable(onClick = onClick)
                .padding(start = 11.dp, end = 13.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(7.dp),
        ) { content() }
    }
}

@Composable
private fun MapButtons(model: CupertinoModel, backdrop: Backdrop, modifier: Modifier) {
    Column(modifier.width(46.dp).liquidGlass(backdrop, shape = roundedGlass(23.dp))) {
        Box(Modifier.size(46.dp).glassClickable { model.environment?.locate() }, contentAlignment = Alignment.Center) {
            VIcon(Res.drawable.ic_near_me_filled, size = 22.dp, tint = VentrovaColors.LocationIos)
        }
        Box(Modifier.padding(horizontal = 10.dp).fillMaxWidth().height(0.5.dp).background(VentrovaColors.Ink.copy(alpha = 0.15f)))
        Box(Modifier.size(46.dp).glassClickable(onClick = model.onOpenSuggest), contentAlignment = Alignment.Center) {
            VIcon(Res.drawable.ic_add_location_alt, size = 22.dp, tint = VentrovaColors.Ink)
        }
    }
}

@Composable
private fun PlacesPanel(model: CupertinoModel, backdrop: Backdrop, available: Dp, modifier: Modifier) {
    val state by model.viewModel.state.collectAsState()
    val detent by model.detent.collectAsState()
    val target = when {
        state.selected != null -> 150.dp
        detent == PanelDetent.Peek -> 74.dp
        detent == PanelDetent.Half -> available * 0.47f
        else -> available - 260.dp
    }
    val height by animateDpAsState(target, spring(dampingRatio = 0.9f, stiffness = 380f))
    Box(modifier.fillMaxWidth().height(height).liquidGlass(backdrop, shape = roundedGlass(30.dp), surface = VentrovaColors.Paper.copy(alpha = 0.78f), refraction = 10.dp)) {
        CupertinoPanelContent(model)
    }
}

@Composable
private fun TabBar(selected: CupertinoTab, onSelect: (CupertinoTab) -> Unit, backdrop: Backdrop, modifier: Modifier) {
    Box(modifier.fillMaxWidth().padding(start = 16.dp, end = 16.dp, bottom = 24.dp).height(64.dp)) {
        Row(
            Modifier.padding(end = 76.dp).fillMaxSize().liquidGlass(backdrop, surface = Color.White.copy(alpha = 0.45f)).padding(4.dp),
        ) {
            listOf(CupertinoTab.Map, CupertinoTab.Favorites, CupertinoTab.Profile).forEach { tab ->
                val active = tab == selected
                val color = if (active) VentrovaColors.TabActive else VentrovaColors.Ink
                Column(
                    Modifier
                        .weight(1f)
                        .fillMaxSize()
                        .clip(RoundedCornerShape(50))
                        .background(if (active) VentrovaColors.Ink.copy(alpha = 0.08f) else Color.Transparent)
                        .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onSelect(tab) },
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(2.dp, Alignment.CenterVertically),
                ) {
                    VIcon(if (active) tab.selectedIcon else tab.icon, size = 24.dp, tint = color)
                    Text(stringResource(tab.label), style = bodyStyle(10.5.sp, FontWeight.SemiBold, color))
                }
            }
        }
        val searching = selected == CupertinoTab.Search
        Box(
            Modifier
                .align(Alignment.CenterEnd)
                .size(64.dp)
                .liquidGlass(backdrop, surface = if (searching) VentrovaColors.Ink else Color.White.copy(alpha = 0.45f))
                .glassClickable { onSelect(if (searching) CupertinoTab.Map else CupertinoTab.Search) },
            contentAlignment = Alignment.Center,
        ) {
            VIcon(Res.drawable.ic_search, size = 26.dp, tint = if (searching) Color.White else VentrovaColors.Ink)
        }
    }
}

/**
 * A page sheet sliding up from the bottom (or a floating card for [floating]), dismissed by tapping
 * the dimmed background or dragging the grabber down. The [content] is the backdrop for the glass
 * buttons in [overlay]; glass must stay outside the layer it refracts.
 */
@Composable
private fun Sheet(
    visible: Boolean,
    onDismiss: () -> Unit,
    background: Color,
    floating: Boolean = false,
    overlay: @Composable BoxScope.(sheetBackdrop: Backdrop) -> Unit = {},
    content: @Composable () -> Unit,
) {
    AnimatedVisibility(visible = visible, enter = fadeIn(tween(250)), exit = fadeOut(tween(250))) {
        Box(Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.28f)).clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onDismiss))
    }
    AnimatedVisibility(
        visible = visible,
        enter = slideInVertically(spring(dampingRatio = 0.9f, stiffness = 300f)) { it },
        exit = slideOutVertically(tween(300)) { it },
    ) {
        var drag by remember { mutableStateOf(0f) }
        val shape = if (floating) RoundedCornerShape(40.dp) else RoundedCornerShape(topStart = 38.dp, topEnd = 38.dp)
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.BottomCenter) {
            Box(
                Modifier
                    .then(if (floating) Modifier.padding(8.dp).fillMaxWidth() else Modifier.padding(top = 54.dp).fillMaxSize())
                    .offset(y = with(LocalDensity.current) { drag.coerceAtLeast(0f).toDp() })
                    .shadow(24.dp, shape)
                    .clip(shape)
                    .background(background)
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {},
            ) {
                val sheetBackdrop = rememberLayerBackdrop()
                Box(Modifier.then(if (floating) Modifier else Modifier.fillMaxSize()).layerBackdrop(sheetBackdrop)) {
                    content()
                }
                // Narrow drag handle, below the overlay so it never covers the sheet's buttons.
                Box(
                    Modifier
                        .align(Alignment.TopCenter)
                        .width(120.dp)
                        .height(28.dp)
                        .pointerInput(Unit) {
                            detectVerticalDragGestures(
                                onDragEnd = {
                                    if (drag > 120f) onDismiss()
                                    drag = 0f
                                },
                                onVerticalDrag = { _, amount -> drag += amount },
                            )
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    Box(Modifier.width(36.dp).height(5.dp).background(VentrovaColors.Ink.copy(alpha = 0.18f), RoundedCornerShape(3.dp)))
                }
                overlay(sheetBackdrop)
            }
        }
    }
}

@Composable
private fun SheetHeader(title: String, leading: @Composable () -> Unit, trailing: @Composable () -> Unit) {
    Box(Modifier.fillMaxWidth().padding(start = 18.dp, end = 18.dp, top = 22.dp, bottom = 12.dp)) {
        Box(Modifier.align(Alignment.CenterStart)) { leading() }
        Text(title, style = bodyStyle(17.sp, FontWeight.SemiBold), modifier = Modifier.align(Alignment.Center))
        Box(Modifier.align(Alignment.CenterEnd)) { trailing() }
    }
}

@Composable
private fun TextAction(text: String, color: Color, onClick: () -> Unit) {
    Text(text, style = bodyStyle(16.sp, color = color), modifier = Modifier.clickable(onClick = onClick))
}

@Composable
private fun PillAction(text: String, background: Color, content: Color, bold: Boolean = false, onClick: () -> Unit) {
    Box(
        Modifier.height(38.dp).clip(RoundedCornerShape(50)).background(background).clickable(onClick = onClick).padding(horizontal = 14.dp),
        contentAlignment = Alignment.Center,
    ) {
        Text(text, style = bodyStyle(16.sp, if (bold) FontWeight.SemiBold else FontWeight.Normal, content))
    }
}

@Composable
private fun GlassIconButton(icon: DrawableResource, backdrop: Backdrop, size: Dp = 38.dp, tint: Color = VentrovaColors.Ink, onClick: () -> Unit) {
    Box(
        Modifier.size(size).liquidGlass(backdrop, surface = Color.White.copy(alpha = 0.55f), refraction = 8.dp).glassClickable(onClick = onClick),
        contentAlignment = Alignment.Center,
    ) {
        VIcon(icon, size = (size.value * 0.53f).dp, tint = tint)
    }
}

@Composable
private fun CloseCircle(onClick: () -> Unit) {
    Box(Modifier.size(32.dp).clip(CircleShape).background(VentrovaColors.Ink.copy(alpha = 0.07f)).clickable(onClick = onClick), contentAlignment = Alignment.Center) {
        VIcon(Res.drawable.ic_close, size = 18.dp, tint = VentrovaColors.Ink)
    }
}

@Composable
private fun Toast(text: String, onShown: () -> Unit, modifier: Modifier) {
    LaunchedEffect(text) {
        delay(2200)
        onShown()
    }
    Box(
        modifier.height(42.dp).shadow(12.dp, RoundedCornerShape(50)).background(VentrovaColors.Ink.copy(alpha = 0.86f), RoundedCornerShape(50)).padding(horizontal = 18.dp),
        contentAlignment = Alignment.Center,
    ) {
        Text(text, style = bodyStyle(14.5.sp, FontWeight.Medium, Color.White), textAlign = TextAlign.Center)
    }
}
